import React from 'react'

import {
  Body2,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  FontFamily,
  FontWeight,
  Image,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { DashboardExperienceRecipeResponse } from '@library/api-dashboard-experience'

type RecommendationRecipeCardProps = {
  onClick: () => void
  recipe: DashboardExperienceRecipeResponse
}

const RecipeCard = withCitrusTheme(Box, () => ({
  backgroundColor: Color.White,
  borderRadius: '4px',
  border: `1px solid ${Color.ColdGrey_100}`,
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.10)',
  overflow: 'hidden',
  scrollSnapAlign: 'start',
}))

const dietaryAcronyms: Record<string, string> = {
  vegetarian: 'V',
  vegan: 'PB',
  'dairy-free': 'DF',
  'gluten-free': 'GF',
}

const getDietaryLabels = (recipe: DashboardExperienceRecipeResponse) =>
  recipe.dietary_claims
    .map((claim) => dietaryAcronyms[claim.slug])
    .filter((label): label is string => Boolean(label))

const getRecipeMeta = (recipe: DashboardExperienceRecipeResponse) => {
  const details = []

  if (typeof recipe.cook_time === 'number') {
    details.push(`${recipe.cook_time} mins`)
  }

  if (typeof recipe.kcal === 'number') {
    details.push(`${recipe.kcal} kcal`)
  }

  return details.join(' | ')
}

export const RecommendationRecipeCard = ({ onClick, recipe }: RecommendationRecipeCardProps) => {
  const dietaryLabels = getDietaryLabels(recipe)
  const recipeMeta = getRecipeMeta(recipe)
  const accessibleLabel = recipe.accessibility.label || recipe.name

  return (
    <RecipeCard
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      flexGrow={0}
      flexShrink={0}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onClick()
        }
      }}
      width={['204px', '204px', '220px', '220px']}
      style={{ alignSelf: 'stretch', cursor: 'pointer' }}
    >
      <Box height="136px" position={DisplayPosition.Relative}>
        <Image
          alt={accessibleLabel}
          fit="cover"
          height="136px"
          src={recipe.image_url}
          width="100%"
          style={{ display: 'block' }}
        />
        {dietaryLabels.length > 0 ? (
          <Box
            display={Display.Flex}
            gap={1}
            left="8px"
            position={DisplayPosition.Absolute}
            paddingH={1}
            paddingV={1}
            top="8px"
            bg={Color.ColdGrey_800}
            style={{ borderRadius: '100%' }}
          >
            {dietaryLabels.map((label) => (
              <Text
                key={`${recipe.id}-${label}`}
                color={Color.White}
                fontFamily={FontFamily.Bold}
                fontWeight={FontWeight.Bold}
                size={1}
                style={{
                  borderRadius: '100%',
                  backgroundColor: Color.ColdGrey_700,
                  width: '24px',
                  height: '24px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {label}
              </Text>
            ))}
          </Box>
        ) : null}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={4}
        paddingH={3}
        paddingV={3}
        justifyContent={JustifyContent.SpaceBetween}
        height={'100%'}
      >
        <Body2 color={Color.ColdGrey_900} fontWeight={FontWeight.Bold} size={1}>
          {recipe.name}
        </Body2>
        {recipeMeta ? (
          <Text color={Color.ColdGrey_600} size={1}>
            {recipeMeta}
          </Text>
        ) : null}
      </Box>
    </RecipeCard>
  )
}
